import React, { ReactNode, useRef } from "react";
import cn from "classnames";
import { gsap, prefersReducedMotion, SETTLE, useGSAP } from "../../motion/gsap";
import SplitText from "../../motion/SplitText";
import styles from "./styles.module.scss";

interface PageProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Controls on the right of the title row (buttons, search). */
  actions?: ReactNode;
  /** Content above the title, e.g. a back link. */
  extra?: ReactNode;
  children?: ReactNode;
  className?: string;
  /**
   * The card grows no taller than the screen, so a DataTable inside scrolls
   * by itself and keeps its header and Totals row in sight.
   */
  fitScreen?: boolean;
}

/**
 * A screen of the admin drawn the way the client draws its pages (Activity,
 * My orders): one white card with the 28/36 title at the top.
 *
 * Opening a screen plays its entrance once: the card rises into place, the
 * title's words come up out of a mask, and the blocks under it follow one
 * after another — so the eye lands on the title first.
 */
const Page: React.FC<PageProps> = ({
  title,
  subtitle,
  actions,
  extra,
  children,
  className,
  fitScreen,
}) => {
  const ref = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const page = ref.current;
      if (!page || prefersReducedMotion()) return;
      const blocks = Array.from(page.children).filter(
        (child) => child.tagName !== "HEADER",
      );
      const actionsRow = page.querySelector(`.${styles.actions}`);
      const tl = gsap.timeline();
      tl.from(page, { y: 16, autoAlpha: 0, duration: 0.5, clearProps: SETTLE });
      if (actionsRow)
        tl.from(
          actionsRow,
          { y: 8, autoAlpha: 0, duration: 0.4, clearProps: SETTLE },
          0.15,
        );
      if (blocks.length)
        tl.from(
          blocks,
          {
            y: 12,
            autoAlpha: 0,
            duration: 0.45,
            stagger: 0.06,
            clearProps: SETTLE,
          },
          0.18,
        );
    },
    { scope: ref },
  );

  return (
    <section
      ref={ref}
      className={cn(styles.page, { [styles.fitScreen]: fitScreen }, className)}
    >
      {extra}
      {(title || actions) && (
        <header className={styles.header}>
          <div className={styles.heading}>
            {typeof title === "string" ? (
              <SplitText
                tag="h1"
                text={title}
                className={styles.title}
                delay={0.08}
              />
            ) : (
              title && <h1 className={styles.title}>{title}</h1>
            )}
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
};

export default Page;
